// Metric cards grid for the Summary view.
// Presentation-only — receives the already-computed stats object (buildSummaryStats)
// from SummaryView.js (same pattern as SummaryHero/BodyweightGoal) and formats it
// into 8 at-a-glance cards.

import React, { memo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import ToolTip from "../../../../../parts/tooltip";
import { adhColor } from "../../../../../utils/nutrition-progress";
import { MUTED, toDisplayWeight, GOAL_STATE_COLORS, fmtSleep, fmtUtc } from "./viewTokens";

/* ---- i18n helper --------------------------------------------------------- */

const m = (t, key, opts) => t(`nutritionPage.progress.metrics.${key}`, opts);

/* ---- helpers ------------------------------------------------------------ */

const logRateToken = (rate) => {
  if (rate >= 85) return "good";
  if (rate >= 60) return "warn";
  return "bad";
};

const pctOrDash = (v) => (v == null ? "—" : `${v}%`);
const numOrDash = (v) => (v == null ? "—" : v.toLocaleString());
const adhToken = (v) => (v == null ? undefined : adhColor(v));

const formatWeightCard = (stats, units, t) => {
  const isImperial = units === "imperial";
  const unit = isImperial ? "lb" : "kg";
  const last = toDisplayWeight(stats.weightLast, isImperial);
  const delta = toDisplayWeight(stats.weightDelta, isImperial);
  const value = last == null ? "—" : `${last} ${unit}`;
  const sign = stats.weightDelta >= 0 ? "+" : "";
  const sub =
    delta == null
      ? undefined
      : m(t, "weightSub", { sign, value: delta, unit });
  return { value, sub };
};

/**
 * Format the steps and sleep sub value.
 * @param {Object} stats - The stats object.
 * @param {function} t - The translation function.
 * @param {boolean} isOpenRange - Whether the range is open.
 * @returns {Object} The formatted steps and sleep sub value.
 */
const formatStepsSleepSub = (stats, t, isOpenRange) => {
  const sleepStr = fmtSleep(stats.avgSleep);
  const trainingDaysKey = isOpenRange
    ? "trainingDaysSubSoFar"
    : "trainingDaysSub";
  const trainingDaysStr = stats.workoutDays
    ? m(t, trainingDaysKey, { count: stats.workoutDays })
    : null;
  const parts = [sleepStr === "—" ? null : sleepStr, trainingDaysStr].filter(
    Boolean
  );
  return {
    sub: parts.join(" · ") || undefined,
    trainingDaysStr,
  };
};

/**
 * Format the rest adherence value.
 * @param {Object} stats - The stats object.
 * @param {function} t - The translation function.
 * @returns {Object} The formatted rest adherence value.
 */
const formatRestCard = (stats, t) => {
  if (!stats.hasRestData) {
    return { value: "—", sub: m(t, "noRestTargetsSet"), token: undefined };
  }
  return {
    value: pctOrDash(stats.restAdh),
    sub: m(t, "vsRestTargets"),
    token: adhColor(stats.restAdh),
  };
};

/**
 * Format the average intake value.
 * @param {number} avgIntake - The average intake value.
 * @param {function} t - The translation function.
 * @returns {React.ReactNode} The formatted average intake value.
 */
const formatAvgIntake = (avgIntake, t) => {
  if (avgIntake == null) {
    return "—";
  }
  return (
    <>
      {avgIntake.toLocaleString()}{" "}
      <span className="np-metric-cards__value-unit">
        {t("common:units.kcalAbbr")}
      </span>
    </>
  );
};

/* ---- metric card ---------------------------------------------------------- */

// `subInfo` (+ the `infoId` that scopes its tooltip instance) appends a ⓘ
// after the sub-label — used by the counts that stop at today while the
// selected range runs past it, so the number can explain itself instead of
// silently disagreeing with a coach counting the calendar. The sub wrapper is
// a <div>, not a <p>: ToolTip renders divs, which a <p> can't legally hold.
const MetricCard = ({ label, value, sub, token, subInfo, infoId }) => {
  const tokenColor = token ? GOAL_STATE_COLORS[token]?.text : null;
  return (
    <div
      className="np-metric-cards__card"
      style={{ borderLeft: `4px solid ${tokenColor ?? "transparent"}` }}
    >
      <p className="np-metric-cards__label" style={{ color: MUTED }}>{label}</p>
      <p className="np-metric-cards__value">{value}</p>
      {sub && (
        <div className="np-metric-cards__sub" style={{ color: tokenColor ?? "var(--text-primary)" }}>
          <span>{sub}</span>
          {subInfo && (
            <ToolTip
              id={infoId}
              body={subInfo}
              className="np-metric-cards__info"
              hintClassNames="np-metric-cards__info-tooltip"
            >
              {/* role="img" so the aria-label is actually announced — a bare
                  span's implicit role doesn't support one, and the raw ⓘ
                  glyph reads as meaningless character noise without it. */}
              <span className="np-metric-cards__info-icon" role="img" aria-label={subInfo}>ⓘ</span>
            </ToolTip>
          )}
        </div>
      )}
    </div>
  );
};

MetricCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  sub: PropTypes.string,
  token: PropTypes.string,
  subInfo: PropTypes.string,
  infoId: PropTypes.string,
};

/* ---- exported grid --------------------------------------------------------- */

export const MetricCards = memo(({ stats, units }) => {
  const { t } = useTranslation(["athlete-portal", "common"]);
  const weight = formatWeightCard(stats, units, t);
  const proteinSub = stats.proteinHitOf
    ? m(t, "proteinHitRateSub", { days: stats.proteinHitDays, of: stats.proteinHitOf })
    : undefined;

  // Both counts below stop at today (buildSummaryStats' `elapsed`). When the
  // selected range still runs past today, they say "so far" and carry a ⓘ —
  // one shared explanation for both cards, so the rule reads the same
  // wherever it applies. Once the range has ended, neither is needed.
  const isOpenRange = stats.rangeEndsInFuture;
  const openRangeInfo = isOpenRange
    ? m(t, "openRangeInfo", { date: fmtUtc(stats.rangeEndUtc, "MMM D") })
    : undefined;
  const loggingRateSubKey = isOpenRange ? "loggingRateSubSoFar" : "loggingRateSub";
  const stepsSleep = formatStepsSleepSub(stats, t, isOpenRange);
  const rest = formatRestCard(stats, t);

  return (
    <div className="np-metric-cards mt-6">
      <MetricCard
        label={m(t, "loggingRate")}
        value={`${stats.loggingRate}%`}
        sub={m(t, loggingRateSubKey, {
          logged: stats.loggedDays,
          total: stats.totalDays,
        })}
        subInfo={openRangeInfo}
        infoId="np-metric-logging-rate-info"
        token={logRateToken(stats.loggingRate)}
      />
      <MetricCard
        label={m(t, "proteinHitRate")}
        value={pctOrDash(stats.proteinHitRate)}
        sub={proteinSub}
        token={adhToken(stats.proteinHitRate)}
      />
      <MetricCard
        label={m(t, "overallAdherence")}
        value={pctOrDash(stats.overallAdh)}
        sub={m(t, "acrossMacros")}
        token={adhToken(stats.overallAdh)}
      />
      <MetricCard label={m(t, "bodyweight")} value={weight.value} sub={weight.sub} />
      <MetricCard
        label={m(t, "trainingDayAdherence")}
        value={pctOrDash(stats.trainingAdh)}
        sub={m(t, "vsTrainingTargets")}
        token={adhToken(stats.trainingAdh)}
      />
      <MetricCard
        label={m(t, "restDayAdherence")}
        value={rest.value}
        sub={rest.sub}
        token={rest.token}
      />
      <MetricCard
        label={m(t, "avgIntake")}
        value={formatAvgIntake(stats.avgIntake, t)}
        sub={m(t, "loggedDaysSub")}
      />
      <MetricCard
        label={m(t, "avgStepsSleep")}
        value={numOrDash(stats.avgSteps)}
        sub={stepsSleep.sub}
        subInfo={stepsSleep.trainingDaysStr ? openRangeInfo : undefined}
        infoId="np-metric-steps-sleep-info"
      />
    </div>
  );
});

MetricCards.propTypes = {
  stats: PropTypes.object.isRequired,
  units: PropTypes.string,
};
